import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { GEO_DISCLAIMER } from "@/lib/siteContent";
import { CmsSplitPreview } from "@/pages/admin/CmsLivePreview";
import { SearchDocView } from "@/pages/search/SearchDocLayout";
import {
  AdminEditDrawer,
  AdminEmptyState,
  AdminField,
  AdminPagination,
  paginateItems,
  previewText,
} from "@/pages/admin/AdminUx";
import {
  AdminSaveStatus,
  AdminViewModeToggle,
  EvidenceFlow,
  EvidenceMark,
  SeoLabeledField,
  SeoLivePreview,
  SurfaceBadges,
  useSeoAdminView,
} from "@/pages/admin/SeoAeoGeoUx.jsx";
import {
  AUTHORITY_EVIDENCE_LABEL,
  AUTHORITY_PUBLIC_LABEL,
  CAPABILITY_LABELS,
  IDENTITY_LABEL,
  QUERY_FIELDS,
  authoritySurfaces,
  isUnpublished,
} from "@/pages/admin/seoAeoGeoUx";

const GUIDE_HINTS = {
  title: "공개 페이지 맨 위 큰 제목(H1)이자 Google/Naver 검색 결과 제목의 기본값입니다. 약 20~50자 권장.",
  slug: "공개 주소 /guides/{slug} 에 사용됩니다. 영문 소문자와 하이픈만 권장합니다. 예: homepage-cost-factors",
  summary: "검색 결과 설명과 페이지 상단 요약에 쓰입니다. 약 80~150자 권장.",
  question: "이 글이 답하는 검색 질문입니다. AI 답변 최적화의 핵심입니다. 예: 병원 홈페이지에는 어떤 기능이 필요한가요?",
  answer: "첫 화면의 핵심 답변입니다. 공개 화면과 AI가 모두 참고합니다. 마케팅 문구만 쓰지 말고 바로 답하세요.",
  status: "공개(published)만 홈페이지·sitemap에 들어갑니다. 초안(draft)은 검색에 아직 반영되지 않습니다.",
  robots: "index,follow이면 검색에 나갑니다. 초안은 noindex를 권장합니다. 공개 화면에는 보이지 않습니다.",
};

function asLines(value) {
  if (Array.isArray(value)) return value.join("\n");
  return value || "";
}

function parseLines(value) {
  return String(value || "").split("\n").map((row) => row.trim()).filter(Boolean);
}

function emptyGuide() {
  return {
    title: "",
    slug: "",
    category: "homepage",
    summary: "",
    question: "",
    answer: "",
    body_blocks: [{ type: "p", text: "" }],
    related_questions: [],
    faqs: [{ question: "", answer: "" }],
    service_ids: [],
    portfolio_slugs: [],
    query_ids: [],
    status: "draft",
    robots: "index,follow",
    seo_title: "",
    meta_description: "",
    cta_label: "프로젝트 상담",
    cta_href: "/#contact",
    author_name: "DSITESOLUTION",
  };
}

function emptyService() {
  return {
    title: "",
    slug: "",
    eyebrow: "SERVICE",
    summary: "",
    question: "",
    answer: "",
    scope: [],
    features: [],
    body_blocks: [{ type: "p", text: "" }],
    faqs: [{ question: "", answer: "" }],
    portfolio_slugs: [],
    guide_ids: [],
    status: "draft",
    robots: "index,follow",
    seo_title: "",
    meta_description: "",
    cta_label: "이 서비스 상담하기",
    cta_href: "/#contact",
  };
}

function GuideEditor({ draft, setDraft, services, portfolios, queries, view }) {
  const bodyText = (draft.body_blocks || []).map((b) => (b.type === "h2" ? `## ${b.text}` : b.type === "ul" ? (b.items || []).map((i) => `- ${i}`).join("\n") : b.type === "ol" ? (b.items || []).map((i, idx) => `${idx + 1}. ${i}`).join("\n") : b.text)).join("\n\n");
  const setBody = (text) => {
    const blocks = [];
    String(text || "").split(/\n{2,}/).forEach((chunk) => {
      const lines = chunk.split("\n").map((l) => l.trim()).filter(Boolean);
      if (!lines.length) return;
      if (lines[0].startsWith("## ")) blocks.push({ type: "h2", text: lines[0].slice(3) });
      else if (lines.every((l) => l.startsWith("- "))) blocks.push({ type: "ul", items: lines.map((l) => l.slice(2)) });
      else if (lines.every((l) => /^\d+\.\s/.test(l))) blocks.push({ type: "ol", items: lines.map((l) => l.replace(/^\d+\.\s/, "")) });
      else blocks.push({ type: "p", text: lines.join("\n") });
    });
    setDraft({ ...draft, body_blocks: blocks });
  };
  return (
    <div className="space-y-4">
      <AdminField label={view === "dev" ? "제목 (title)" : "제목"} hint={GUIDE_HINTS.title}><input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="guide-title" /></AdminField>
      <AdminField label={view === "dev" ? "주소 이름 (slug)" : "주소 이름"} hint={GUIDE_HINTS.slug}><input value={draft.slug || ""} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></AdminField>
      <AdminField label="공개 주소" hint="실제로 열리는 페이지 주소입니다. 주소 이름을 바꾸면 함께 바뀝니다.">
        <input readOnly value={draft.path || `/guides/${draft.slug || ""}`} />
      </AdminField>
      <AdminField label={view === "dev" ? "카테고리 (category)" : "카테고리"} hint="가이드 목록의 필터 버튼에 이렇게 보입니다.">
        <select value={draft.category || "homepage"} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
          <option value="homepage">홈페이지</option>
          <option value="hospital">병원</option>
          <option value="webdev">웹개발</option>
          <option value="platform">웹·CMS</option>
          <option value="ai">AI·자동화</option>
          <option value="automation">자동화</option>
          <option value="saas">SaaS</option>
          <option value="search">SEO·AEO·GEO</option>
        </select>
      </AdminField>
      <AdminField label={view === "dev" ? "요약 (summary)" : "요약"} hint={GUIDE_HINTS.summary}><textarea rows={3} value={draft.summary || ""} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "질문 (question)" : "이 페이지가 답하는 질문"} hint={GUIDE_HINTS.question}><input value={draft.question || ""} onChange={(e) => setDraft({ ...draft, question: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "핵심 답변 (answer)" : "핵심 답변"} hint={GUIDE_HINTS.answer}><textarea rows={5} value={draft.answer || ""} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} data-testid="guide-answer" /></AdminField>
      <AdminField label={view === "dev" ? "작업 범위 (scope)" : "작업 범위"} hint="공개 페이지에 보이는 범위입니다. 한 줄에 하나씩 적으세요."><textarea rows={3} value={asLines(draft.scope)} onChange={(e) => setDraft({ ...draft, scope: parseLines(e.target.value) })} /></AdminField>
      <AdminField label={view === "dev" ? "기능 (features)" : "기능"} hint="공개 페이지에 보이는 기능 목록입니다. 한 줄에 하나씩 적으세요."><textarea rows={3} value={asLines(draft.features)} onChange={(e) => setDraft({ ...draft, features: parseLines(e.target.value) })} /></AdminField>
      <AdminField label={view === "dev" ? "본문 (body_blocks)" : "본문"} hint="공개 가이드 페이지에 보입니다. 빈 줄로 문단을 나눕니다. ## 제목, - 목록, 1. 단계."><textarea rows={10} value={bodyText} onChange={(e) => setBody(e.target.value)} /></AdminField>
      <AdminField label={view === "dev" ? "관련 질문 (related_questions)" : "관련 질문"} hint="페이지 하단에 보이는 관련 질문입니다. 한 줄에 하나씩."><textarea rows={3} value={asLines(draft.related_questions)} onChange={(e) => setDraft({ ...draft, related_questions: parseLines(e.target.value) })} /></AdminField>
      <AdminField label={view === "dev" ? "관련 서비스 ID (service_ids)" : "연결된 서비스"} hint="이 글과 연결할 서비스입니다. 공개 화면과 검색 근거에 쓰입니다.">
        <textarea rows={3} value={asLines(draft.service_ids)} onChange={(e) => setDraft({ ...draft, service_ids: parseLines(e.target.value) })} />
        <p className="text-[11px] text-white/35 mt-1">{(services || []).map((s) => view === "dev" ? s.id : (s.title || s.id)).filter(Boolean).join(", ")}</p>
      </AdminField>
      <AdminField label={view === "dev" ? "관련 포트폴리오 slug (portfolio_slugs)" : "연결된 포트폴리오"} hint="공개 프로젝트만 연결하세요. 검색 근거와 관련 작업에 표시됩니다.">
        <textarea rows={2} value={asLines(draft.portfolio_slugs)} onChange={(e) => setDraft({ ...draft, portfolio_slugs: parseLines(e.target.value) })} />
        <p className="text-[11px] text-white/35 mt-1">{(portfolios || []).map((p) => p.slug).filter(Boolean).join(", ")}</p>
      </AdminField>
      <AdminField label={view === "dev" ? "Target Query ID (query_ids)" : "연결된 검색 질문"} hint="노출시키고 싶은 검색 질문과 이 글을 연결합니다."><textarea rows={2} value={asLines(draft.query_ids)} onChange={(e) => setDraft({ ...draft, query_ids: parseLines(e.target.value) })} /></AdminField>
      <AdminField label={view === "dev" ? "공개 상태 (status)" : "공개 상태"} hint={GUIDE_HINTS.status}>
        <select value={draft.status || "draft"} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
          <option value="draft">초안 · 검색에 아직 반영되지 않음</option>
          <option value="published">공개</option>
        </select>
      </AdminField>
      <AdminField label={view === "dev" ? "robots" : "검색 수집"} hint={GUIDE_HINTS.robots}><input value={draft.robots || "index,follow"} onChange={(e) => setDraft({ ...draft, robots: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "SEO Title (seo_title)" : "검색 결과 제목"} hint="Google/Naver 검색 결과의 제목으로 사용됩니다. 비워 두면 위 제목을 씁니다."><input value={draft.seo_title || ""} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "Meta Description (meta_description)" : "검색 결과 설명"} hint="검색 결과 제목 아래에 표시될 설명입니다. 약 80~150자 권장."><textarea rows={3} value={draft.meta_description || ""} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "canonical" : "대표 URL"} hint="검색엔진에 이 페이지의 공식 주소로 알려주는 URL입니다. 비워 두면 공개 주소를 사용합니다."><input value={draft.canonical || ""} onChange={(e) => setDraft({ ...draft, canonical: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "og_title" : "공유 제목"} hint="카카오톡·SNS로 링크를 공유할 때 표시되는 제목입니다."><input value={draft.og_title || ""} onChange={(e) => setDraft({ ...draft, og_title: e.target.value })} /></AdminField>
      <AdminField label={view === "dev" ? "og_description" : "공유 설명"} hint="SNS 공유 카드에 표시되는 설명입니다."><textarea rows={2} value={draft.og_description || ""} onChange={(e) => setDraft({ ...draft, og_description: e.target.value })} /></AdminField>
      {view === "dev" ? (
        <AdminField label="id / path"><input readOnly value={`${draft.id || ""} ${draft.path || ""}`} /></AdminField>
      ) : null}
    </div>
  );
}

export function SearchContentCms({ kind = "guides" }) {
  const isGuide = kind === "guides";
  const ctx = useSeoAdminView();
  const [localView, setLocalView] = useState("simple");
  const view = ctx.hasProvider ? ctx.view : localView;
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [queries, setQueries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    const [g, s, p, qry] = await Promise.all([
      api.get(`/admin/search/${kind}`),
      api.get("/admin/search/services"),
      api.get("/admin/portfolio/projects"),
      api.get("/admin/search/queries"),
    ]);
    setItems(g.data.items || []);
    setServices(s.data.items || []);
    setPortfolios(p.data.items || []);
    setQueries(qry.data.items || []);
  };

  useEffect(() => { load().catch(() => toast.error("검색 콘텐츠 로드 실패")); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) => `${row.title} ${row.slug} ${row.summary}`.toLowerCase().includes(needle));
  }, [items, q]);
  const paged = paginateItems(filtered, page, 8);
  const draft = editing || (isGuide ? emptyGuide() : emptyService());
  const dirty = JSON.stringify(editing || {}) !== JSON.stringify(saved || {});

  const startNew = () => {
    const next = isGuide ? emptyGuide() : emptyService();
    setEditing(next);
    setSaved(next);
  };
  const startEdit = (row) => {
    setEditing({ ...row });
    setSaved({ ...row });
  };
  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) await api.put(`/admin/search/${kind}/${editing.id}`, editing);
      else {
        const r = await api.post(`/admin/search/${kind}`, editing);
        setEditing(r.data.item);
        setSaved(r.data.item);
      }
      toast.success("DEV에 저장되었습니다. 코드 수정 없이 공개됩니다.");
      await load();
      setSaved({ ...editing });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };

  const previewItem = editing || draft;
  const related = [];
  (previewItem.service_ids || []).forEach((id) => {
    const svc = services.find((s) => s.id === id);
    if (svc) related.push({ href: svc.path, label: svc.title });
  });
  (previewItem.portfolio_slugs || []).forEach((slug) => {
    const p = portfolios.find((row) => row.slug === slug);
    if (p) related.push({ href: p.path || `/portfolio/${slug}`, label: p.title });
  });

  return (
    <div data-testid={`search-cms-${kind}`}>
      {ctx.hasProvider ? null : (
        <div className="flex flex-wrap gap-2 mb-4">
          <button type="button" className={view === "simple" ? "btn-primary" : "admin-row__btn"} onClick={() => setLocalView("simple")}>일반 보기</button>
          <button type="button" className={view === "dev" ? "btn-primary" : "admin-row__btn"} onClick={() => setLocalView("dev")} data-testid="search-dev-view">개발자 보기</button>
        </div>
      )}
      {!editing ? (
        <>
          <div className="admin-toolbar">
            <input className="admin-toolbar__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="검색" />
            <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-sm" onClick={startNew}>+ 작성</button>
            <span className="admin-count">{filtered.length}개</span>
          </div>
          {paged.items.length === 0 ? <AdminEmptyState message="콘텐츠가 없습니다." /> : (
            <div className="admin-row-list">
              {paged.items.map((row) => (
                <div key={row.id} className="admin-row">
                  <div className="admin-row__main">
                    <div className="admin-row__title">{row.title}</div>
                    <div className="admin-row__preview">{previewText(row.summary || row.answer)}</div>
                    <div className="admin-row__meta">
                      {view === "dev" ? `${row.path} · ${row.status}` : (row.status === "published" ? "공개" : "초안")}
                      {isUnpublished(row) ? " · 검색에 아직 반영되지 않음" : ""}
                    </div>
                  </div>
                  <button type="button" className="admin-row__btn" onClick={() => startEdit(row)}>수정</button>
                </div>
              ))}
            </div>
          )}
          <AdminPagination page={paged.page} pageSize={8} total={filtered.length} onPageChange={setPage} />
        </>
      ) : (
        <CmsSplitPreview
          testId="search-cms-split"
          dirty={dirty}
          savedNote={!dirty && saved?.id ? "저장 완료" : ""}
          header={(
            <div>
              <AdminSaveStatus dirty={dirty} saved={!dirty && Boolean(saved?.id)} unpublished={isUnpublished(previewItem)} />
              <p className="text-xs text-white/45 mt-2">입력 중에는 미리보기만 바뀝니다. 저장을 눌러야 Atlas DEV에 반영됩니다.</p>
            </div>
          )}
          editor={(
            <div>
              <div className="seo-ux-sticky">
                <AdminSaveStatus dirty={dirty} saved={!dirty && Boolean(saved?.id)} unpublished={isUnpublished(previewItem)} />
                <div className="seo-ux-sticky__actions">
                  <button type="button" className="btn-primary" onClick={save} data-testid="search-cms-save">저장</button>
                  <button type="button" className="admin-row__btn" onClick={() => setEditing(null)}>목록</button>
                </div>
              </div>
              <SeoLivePreview
                title={previewItem.seo_title || previewItem.title}
                url={`https://dsitesolution.com${previewItem.path || ""}`}
                description={previewItem.meta_description || previewItem.summary}
                ogTitle={previewItem.og_title || previewItem.seo_title || previewItem.title}
                ogDescription={previewItem.og_description || previewItem.meta_description || previewItem.summary}
                ogUrl={`https://dsitesolution.com${previewItem.path || ""}`}
                ogImage={previewItem.og_image}
                aiItems={[
                  { label: "질문", value: previewItem.question },
                  { label: "핵심 답변", value: previewItem.answer },
                  { label: "공개 상태", value: previewItem.status === "published" ? "공개" : "초안" },
                ]}
              />
              <GuideEditor draft={previewItem} setDraft={setEditing} services={isGuide ? services : queries} portfolios={portfolios} queries={queries} view={view} />
              {view === "dev" ? (
                <pre className="mt-4 text-[11px] bg-black/40 border border-white/10 rounded-xl p-3 overflow-auto max-h-64">{JSON.stringify(previewItem, null, 2)}</pre>
              ) : null}
            </div>
          )}
          preview={(
            <div className="ds-search-preview bg-white text-slate-900 h-full overflow-auto p-6" onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <SearchDocView item={previewItem} related={related} kicker={isGuide ? "GUIDE" : previewItem.eyebrow || "SERVICE"} preview />
            </div>
          )}
        />
      )}
    </div>
  );
}

export function TargetQueryPanel() {
  const { view, hasProvider } = useSeoAdminView();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [services, setServices] = useState([]);
  const [guides, setGuides] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [facts, setFacts] = useState([]);
  const load = async () => {
    const [q, ev, s, g, p, f] = await Promise.all([
      api.get("/admin/search/queries"),
      api.get("/admin/search-evidence"),
      api.get("/admin/search/services"),
      api.get("/admin/search/guides"),
      api.get("/admin/portfolio/projects"),
      api.get("/admin/search/verified-facts"),
    ]);
    setItems(q.data.items || []);
    setEvidence(ev.data.items || []);
    setServices(s.data.items || []);
    setGuides(g.data.items || []);
    setPortfolios(p.data.items || []);
    setFacts(f.data.items || []);
  };
  useEffect(() => { load().catch(() => toast.error("질문 로드 실패")); }, []);

  const names = {
    service: Object.fromEntries(services.map((row) => [row.id, row.title || row.name || row.id])),
    guide: Object.fromEntries(guides.map((row) => [row.id, row.title || row.id])),
    portfolio: Object.fromEntries(portfolios.map((row) => [row.slug, row.title || row.slug])),
    authority: Object.fromEntries(facts.map((row) => [row.id, row.public_title || row.title || row.id])),
  };
  const labelList = (kind, ids) => (ids || []).map((id) => names[kind]?.[id] || id).filter(Boolean).join(", ");

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/search/queries/${editing.id}`, editing);
      else await api.post("/admin/search/queries", editing);
      toast.success("저장되었습니다.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };
  return (
    <div data-testid="target-query-panel">
      {hasProvider ? null : <div className="mb-3"><AdminViewModeToggle /></div>}
      <p className="text-xs text-white/50 mb-4">
        {view === "dev"
          ? "AI 추천/검색 목표 질문입니다. 점수 예측은 없습니다. 연결된 근거만 표시합니다."
          : "질문 → 서비스 → 가이드 → 포트폴리오 → 전문성 근거 순으로 연결 상태를 확인합니다. 순위 점수는 없습니다."}
      </p>
      <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-sm mb-4" onClick={() => setEditing({ query: "", intent: "commercial", priority: "HIGH", service_ids: [], guide_ids: [], faq_ids: [], portfolio_slugs: [], landing_path: "" })}>+ 질문 추가</button>
      <div className="space-y-4">
        {items.map((row) => {
          const ev = evidence.find((e) => e.id === row.id);
          return (
            <div key={row.id} className="admin-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <div className="flex justify-between gap-3">
                <div className="admin-row__main">
                  <div className="admin-row__title">{row.query}</div>
                  <div className="admin-row__meta">
                    {view === "dev"
                      ? `${row.id || ""} · ${row.landing_path || ""} · ${row.priority || ""} · ${row.capability_category || ""}`
                      : `${CAPABILITY_LABELS[row.capability_category] || row.capability_category || "미분류"} · ${row.landing_path || "대표 페이지 없음"}`}
                  </div>
                </div>
                <button type="button" className="admin-row__btn" onClick={() => setEditing({ ...row })}>수정</button>
              </div>
              {ev ? (
                <div className="mt-3" data-testid={`evidence-${row.id}`}>
                  <EvidenceFlow
                    query={row.query}
                    marks={ev.marks}
                    quality={ev.quality}
                    view={view}
                    authorityEval={ev.authority_eval}
                    markExplain={ev.mark_explain}
                    linkedLabels={{
                      service: labelList("service", ev.linked?.service_ids || row.service_ids),
                      guide: labelList("guide", ev.linked?.guide_ids || row.guide_ids),
                      portfolio: labelList("portfolio", ev.linked?.portfolio_slugs || row.portfolio_slugs),
                      authority: labelList("authority", ev.linked?.fact_ids),
                    }}
                  />
                  {view === "dev" ? (
                    <div className="text-[11px] text-white/50 mt-2">
                      연결: Service {ev.marks.service} · Guide {ev.marks.guide} · Portfolio {ev.marks.portfolio} · FAQ {ev.marks.faq} · Org {ev.marks.organization}
                      {" · "}내용품질 G {ev.quality?.guide || "-"} / S {ev.quality?.service || "-"}
                      {" · "}근거강도 {ev.evidence_strength || "-"}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <AdminEditDrawer open={Boolean(editing)} title={view === "dev" ? "Target Query" : "검색 질문"} onClose={() => setEditing(null)} footer={<button type="button" className="btn-primary" onClick={save}>저장</button>}>
        {editing ? (
          <>
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.query}
              url={`https://dsitesolution.com${editing.landing_path || ""}`}
              description={`연결된 서비스 ${(editing.service_ids || []).length}개 · 가이드 ${(editing.guide_ids || []).length}개 · 포트폴리오 ${(editing.portfolio_slugs || []).length}개`}
              ogTitle={editing.query}
              ogDescription={editing.landing_path}
              ogUrl={`https://dsitesolution.com${editing.landing_path || ""}`}
              aiItems={[
                { label: "질문", value: editing.query },
                { label: "분류", value: view === "dev" ? editing.capability_category : (CAPABILITY_LABELS[editing.capability_category] || editing.capability_category) },
                { label: "대표 페이지", value: editing.landing_path },
                { label: "연결된 서비스", value: labelList("service", editing.service_ids) },
                { label: "연결된 가이드", value: labelList("guide", editing.guide_ids) },
                { label: "연결된 포트폴리오", value: labelList("portfolio", editing.portfolio_slugs) },
              ]}
            />
            <SeoLabeledField meta={QUERY_FIELDS.query} fieldKey="query" view={view}>
              <input value={editing.query || ""} onChange={(e) => setEditing({ ...editing, query: e.target.value })} data-testid="target-query-input" />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.capability_category} fieldKey="capability_category" view={view}>
              <select value={editing.capability_category || ""} onChange={(e) => setEditing({ ...editing, capability_category: e.target.value })}>
                <option value="">미분류</option>
                {Object.keys(CAPABILITY_LABELS).map((id) => (
                  <option key={id} value={id}>{view === "dev" ? id : CAPABILITY_LABELS[id]}</option>
                ))}
              </select>
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.intent} fieldKey="intent" view={view}>
              <input value={editing.intent || ""} onChange={(e) => setEditing({ ...editing, intent: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.priority} fieldKey="priority" view={view}>
              <input value={editing.priority || ""} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.landing_path} fieldKey="landing_path" view={view}>
              <input value={editing.landing_path || ""} onChange={(e) => setEditing({ ...editing, landing_path: e.target.value })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.service_ids} fieldKey="service_ids" view={view}>
              <textarea rows={2} value={asLines(editing.service_ids)} onChange={(e) => setEditing({ ...editing, service_ids: parseLines(e.target.value) })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.guide_ids} fieldKey="guide_ids" view={view}>
              <textarea rows={2} value={asLines(editing.guide_ids)} onChange={(e) => setEditing({ ...editing, guide_ids: parseLines(e.target.value) })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.faq_ids} fieldKey="faq_ids" view={view}>
              <textarea rows={2} value={asLines(editing.faq_ids)} onChange={(e) => setEditing({ ...editing, faq_ids: parseLines(e.target.value) })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.portfolio_slugs} fieldKey="portfolio_slugs" view={view}>
              <textarea rows={2} value={asLines(editing.portfolio_slugs)} onChange={(e) => setEditing({ ...editing, portfolio_slugs: parseLines(e.target.value) })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.person_ids} fieldKey="person_ids" view={view}>
              <textarea rows={2} value={asLines(editing.person_ids)} onChange={(e) => setEditing({ ...editing, person_ids: parseLines(e.target.value) })} />
            </SeoLabeledField>
            <SeoLabeledField meta={QUERY_FIELDS.credential_ids} fieldKey="credential_ids" view={view}>
              <textarea rows={2} value={asLines(editing.credential_ids)} onChange={(e) => setEditing({ ...editing, credential_ids: parseLines(e.target.value) })} />
            </SeoLabeledField>
            {view === "dev" && editing.id ? (
              <AdminField label="id"><input readOnly value={editing.id} /></AdminField>
            ) : null}
          </>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}

export function EvidenceMapPanel() {
  const { view, hasProvider } = useSeoAdminView();
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [guides, setGuides] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [facts, setFacts] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/admin/search-evidence"),
      api.get("/admin/search/services"),
      api.get("/admin/search/guides"),
      api.get("/admin/portfolio/projects"),
      api.get("/admin/search/verified-facts"),
    ]).then(([ev, s, g, p, f]) => {
      setRows(ev.data.items || []);
      setServices(s.data.items || []);
      setGuides(g.data.items || []);
      setPortfolios(p.data.items || []);
      setFacts(f.data.items || []);
    }).catch(() => toast.error("근거 맵 로드 실패"));
  }, []);
  const names = {
    service: Object.fromEntries(services.map((row) => [row.id, row.title || row.name || row.id])),
    guide: Object.fromEntries(guides.map((row) => [row.id, row.title || row.id])),
    portfolio: Object.fromEntries(portfolios.map((row) => [row.slug, row.title || row.slug])),
    authority: Object.fromEntries(facts.map((row) => [row.id, row.public_title || row.title || row.id])),
  };
  const labelList = (kind, ids) => (ids || []).map((id) => names[kind]?.[id] || id).filter(Boolean).join(", ");
  return (
    <div data-testid="evidence-map">
      {hasProvider ? null : <div className="mb-3"><AdminViewModeToggle /></div>}
      <p className="text-xs text-white/50 mb-3">{GEO_DISCLAIMER}</p>
      <p className="text-xs text-white/40 mb-4">
        O 충분한 근거 · △ 일부 근거 있음 · X 근거 부족.
        {view === "dev" ? " quality는 내용이 질문을 뒷받침하는지, strength는 내부 근거 강도입니다. 가짜 순위/AI 점수는 없습니다." : " △ 또는 X에 마우스를 올리거나 클릭하면, 부족한 점과 O가 되기 위한 조치가 표시됩니다. 순위 점수는 없습니다."}
      </p>
      <div className="space-y-4">
        {rows.map((row) => (
          <EvidenceFlow
            key={row.id}
            query={row.query}
            marks={row.marks}
            quality={row.quality}
            view={view}
            authorityEval={row.authority_eval}
            markExplain={row.mark_explain}
            linkedLabels={{
              service: labelList("service", row.linked?.service_ids),
              guide: labelList("guide", row.linked?.guide_ids),
              portfolio: labelList("portfolio", row.linked?.portfolio_slugs),
              authority: labelList("authority", row.linked?.fact_ids),
            }}
          />
        ))}
      </div>
      <div className="seo-ux-table-wrap">
        <table className="w-full text-sm text-left">
          <thead className="text-white/45 text-xs">
            <tr>
              {(view === "dev"
                ? ["질문", "분류", "Service", "Landing", "Portfolio", "Guide", "FAQ", "Org", "Authority", "Source", "내용품질", "근거강도"]
                : ["질문", "분류", "서비스", "대표 페이지", "포트폴리오", "가이드", "FAQ", "회사", "전문성", "출처", "내용 품질", "근거 강도"]
              ).map((h) => (
                <th key={h} className="p-2 border-b border-white/10">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="p-2 border-b border-white/5">{row.query}<div className="text-[11px] text-white/35">{view === "dev" ? row.capability_category : (CAPABILITY_LABELS[row.capability_category] || row.capability_category || "")}</div></td>
                <td className="p-2 border-b border-white/5">{view === "dev" ? (row.capability_category || "-") : (CAPABILITY_LABELS[row.capability_category] || row.capability_category || "-")}</td>
                {["service", "landing", "portfolio", "guide", "faq", "organization", "authority", "official_source"].map((k) => (
                  <td key={k} className="p-2 border-b border-white/5">
                    <EvidenceMark
                      compact
                      mark={row.marks?.[k]}
                      kind={k}
                      view={view}
                      evalRow={k === "authority" ? row.authority_eval : null}
                      note={row.mark_explain?.[k]}
                    />
                  </td>
                ))}
                <td className="p-2 border-b border-white/5">
                  <div className="seo-ux-quality-marks">
                    <span>G</span>
                    <EvidenceMark compact mark={row.quality?.guide} kind="guide" view={view} note={row.mark_explain?.quality_guide} />
                    <span>S</span>
                    <EvidenceMark compact mark={row.quality?.service} kind="service" view={view} note={row.mark_explain?.quality_service} />
                    <span>P</span>
                    <EvidenceMark compact mark={row.quality?.portfolio} kind="portfolio" view={view} note={row.mark_explain?.quality_portfolio} />
                  </div>
                </td>
                <td className="p-2 border-b border-white/5">{row.evidence_strength}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntityKindPanel({ kind, title, fields }) {
  const { view } = useSeoAdminView();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/search/${kind}`).then((r) => setItems(r.data.items || []));
  useEffect(() => { load().catch(() => {}); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/search/${kind}/${editing.id}`, editing);
      else await api.post(`/admin/search/${kind}`, editing);
      toast.success("저장되었습니다.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };
  return (
    <div className="mb-8" data-testid={`entity-panel-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm text-white/70">{title}</h4>
        <button type="button" className="admin-row__btn" onClick={() => setEditing(fields.reduce((acc, f) => ({ ...acc, [f.key]: f.def || "" }), {}))}>+ 추가</button>
      </div>
      {items.length === 0 ? <AdminEmptyState message={`${title} 없음`} /> : items.map((row) => (
        <div key={row.id} className="admin-row">
          <div className="admin-row__main">
            <div className="admin-row__title">{row.name || row.title}</div>
            <div className="admin-row__preview">{previewText(row.summary || row.proves || row.relation_status)}</div>
          </div>
          <button type="button" className="admin-row__btn" onClick={() => setEditing({ ...row })}>수정</button>
        </div>
      ))}
      <AdminEditDrawer open={Boolean(editing)} title={title} onClose={() => setEditing(null)} footer={<button type="button" className="btn-primary" onClick={save}>저장</button>}>
        {editing ? fields.map((f) => (
          <AdminField key={f.key} label={view === "dev" ? `${f.label} (${f.key})` : f.label} hint={f.hint}>
            {f.area ? (
              <textarea rows={3} value={Array.isArray(editing[f.key]) ? editing[f.key].join("\n") : (editing[f.key] || "")} onChange={(e) => setEditing({ ...editing, [f.key]: f.list ? parseLines(e.target.value) : e.target.value })} />
            ) : (
              <input value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} />
            )}
          </AdminField>
        )) : null}
      </AdminEditDrawer>
    </div>
  );
}

function emptyFact() {
  return {
    public_title: "",
    public_summary: "",
    title: "",
    category: "PERSON_EXPERIENCE",
    subject_type: "organization",
    subject_id: "org-dsitesolution",
    fact_type: "experience",
    issuer: "",
    country: "KR",
    start_date: "",
    end_date: "",
    issued_date: "",
    verification_type: "DOCUMENT_HELD",
    verification_url: "",
    evidence_status: "private",
    public_status: "hidden",
    identity_match: "not_required",
    official_name_status: "not_required",
    public_group: "EXPERIENCE",
    public_label: "",
    metric_value: "",
    metric_unit: "",
    related_capability_ids: [],
    related_services: [],
    related_guides: [],
    related_portfolios: [],
    related_target_queries: [],
    schema_mapping: "",
  };
}

function identityLabel(row, view) {
  const value = row.identity_match || "not_required";
  if (view === "dev") {
    if (value === "pending") return "Identity Match: 확인 필요";
    if (value === "confirmed") return "Identity Match: confirmed";
    if (value === "rejected") return "Identity Match: rejected";
    return "Identity Match: not required";
  }
  return IDENTITY_LABEL[value] || value;
}

function officialNameLabel(row, view) {
  const value = row.official_name_status || "not_required";
  if (value === "pending") return view === "dev" ? "공식 명칭 확인 필요" : "공식 명칭 확인 필요";
  if (value === "confirmed") return view === "dev" ? "공식 명칭 confirmed" : "공식 명칭 확인 완료";
  return "";
}

function evidenceStatusLabel(row, view) {
  const value = row.evidence_status || "private";
  if (view === "dev") return value === "verified" ? "VERIFIED" : "PRIVATE EVIDENCE";
  return AUTHORITY_EVIDENCE_LABEL[value] || value;
}

function publicStatusLabel(row, view) {
  const value = row.public_status || "hidden";
  if (view === "dev") {
    if (value === "approved") return "PUBLIC APPROVED";
    return value.toUpperCase();
  }
  return AUTHORITY_PUBLIC_LABEL[value] || value;
}

function strengthGroups(facts) {
  const has = (id) => facts.some((row) => row.id === id);
  const statusOf = (id) => {
    const row = facts.find((item) => item.id === id);
    if (!row) return "X";
    if (row.identity_match === "pending" || row.official_name_status === "pending") return "Pending";
    if (row.evidence_status === "verified" && row.public_status === "approved") return "Verified";
    if (row.public_status === "draft") return "Draft";
    return "Pending";
  };
  return [
    {
      title: "COMPANY AUTHORITY",
      simple: "회사 전문성",
      items: [
        ["여성기업", "fact-cert-women"],
        ["창업기업", "fact-cert-startup"],
        ["Registered Trademark", "fact-trademark-dsitesolution"],
      ],
    },
    {
      title: "TECHNICAL AUTHORITY",
      simple: "기술 전문성",
      items: [
        ["Full-stack Training", "fact-java-fullstack"],
        ["Full-stack Award", "fact-fullstack-award"],
        ["AI Foundations", "fact-ibm-coursera-ai-foundations"],
      ],
    },
    {
      title: "EDUCATIONAL / PROFESSIONAL",
      simple: "교육 · 전문 자격",
      items: [
        ["AI Development Lecture", "fact-lecture-ai-haeoreum"],
        ["Coding Instructor", "fact-coding-instructor"],
        ["Software Education Instructor", "fact-software-edu-instructor"],
      ],
    },
    {
      title: "PERSON CREDENTIALS",
      simple: "개인 자격",
      items: [
        ["정보처리기사", "fact-engineer-info-processing"],
        ["웹디자인 자격", "fact-web-design-credential"],
      ],
    },
    {
      title: "PROJECT EXPERIENCE",
      simple: "프로젝트 경력",
      items: [
        ["누적 프로젝트 832건", "fact-project-count-832"],
      ],
    },
    {
      title: "IP",
      simple: "지식재산",
      items: [
        ["Registered Trademark", "fact-trademark-dsitesolution"],
        ["Patent Application", "fact-patent-application"],
      ],
    },
    {
      title: "EXTERNAL VERIFICATION",
      simple: "외부 검증",
      items: [
        ["Coursera verification", "fact-ibm-coursera-ai-foundations"],
        ["Credly verification", "fact-ibm-generative-ai-essentials"],
      ],
    },
  ].map((group) => ({
    ...group,
    items: group.items.map(([label, id]) => [label, has(id) ? statusOf(id) : "X"]),
  }));
}

export function AuthorityFactsPanel() {
  const ctx = useSeoAdminView();
  const [localView, setLocalView] = useState("simple");
  const view = ctx.hasProvider ? ctx.view : localView;
  const [facts, setFacts] = useState([]);
  const [privateItems, setPrivateItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [privateDraft, setPrivateDraft] = useState(null);
  const load = async () => {
    const [f, p] = await Promise.all([
      api.get("/admin/search/verified-facts"),
      api.get("/admin/search/private-evidence"),
    ]);
    setFacts(f.data.items || []);
    setPrivateItems(p.data.items || []);
  };
  useEffect(() => { load().catch(() => toast.error("전문성·경력 로드 실패")); }, []);
  const saveFact = async () => {
    try {
      if (editing.id) await api.put(`/admin/search/verified-facts/${editing.id}`, editing);
      else await api.post("/admin/search/verified-facts", editing);
      toast.success("저장되었습니다. 공개 승인 전까지 홈페이지에 나가지 않습니다.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };
  const savePrivate = async () => {
    try {
      if (privateDraft.id) await api.put(`/admin/search/private-evidence/${privateDraft.id}`, privateDraft);
      else await api.post("/admin/search/private-evidence", privateDraft);
      toast.success("내부 증빙만 저장되었습니다. 자동 공개되지 않습니다.");
      setPrivateDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "저장 실패");
    }
  };
  return (
    <div className="mb-10" data-testid="authority-facts-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm text-white/70">{view === "dev" ? "전문성 · 경력 (공개 사실)" : "전문성 · 인증 · 경력"}</h4>
        <div className="flex gap-2">
          {ctx.hasProvider ? null : (
            <button type="button" className="admin-row__btn" onClick={() => setLocalView(view === "simple" ? "dev" : "simple")}>{view === "simple" ? "개발자 보기" : "일반 보기"}</button>
          )}
          <button type="button" className="admin-row__btn" onClick={() => setEditing(emptyFact())}>+ 항목 추가</button>
        </div>
      </div>
      <p className="text-xs text-white/40 mb-3">흐름: 내부 증빙 등록 → 사실 검증(검증 완료) → 본인 확인 / 공식 명칭 확인 → 공개 중. 원본 파일/개인정보는 공개 화면·검색·JSON-LD에 나가지 않습니다.</p>
      {facts.some((row) => row.identity_match === "pending") ? (
        <p className="text-xs text-amber-200/80 mb-3">{view === "dev" ? "Identity Match: 확인 필요" : "본인 확인 필요"} — Generative AI Essentials 등 표시명이 다른 항목은 공개되지 않습니다.</p>
      ) : null}
      {facts.some((row) => row.official_name_status === "pending") ? (
        <p className="text-xs text-amber-200/80 mb-3">공식 명칭 확인 필요 — 웹디자인 자격 등 공식 명칭이 확인되지 않은 항목은 공개되지 않습니다. 웹디자인기능사·GTQ 등 추정 명칭을 넣지 마세요.</p>
      ) : null}
      <div className="mb-6 grid gap-3 md:grid-cols-2">
        {strengthGroups(facts).map((group) => (
          <div key={group.title} className="rounded-xl border border-white/10 p-3">
            <div className="text-[11px] tracking-[0.16em] uppercase text-white/45 mb-2">{view === "dev" ? group.title : (group.simple || group.title)}</div>
            {group.items.map(([label, status]) => (
              <div key={label} className="flex justify-between text-xs text-white/70 py-0.5">
                <span>{label}</span>
                <span>{status}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {facts.length === 0 ? <AdminEmptyState message="공개 사실 없음" /> : facts.map((row) => (
        <div key={row.id} className="admin-row">
          <div className="admin-row__main">
            <div className="admin-row__title">{row.public_title || row.title}</div>
            <div className="admin-row__meta">
              {evidenceStatusLabel(row, view)} · {publicStatusLabel(row, view)} · {identityLabel(row, view)}
              {officialNameLabel(row, view) ? ` · ${officialNameLabel(row, view)}` : ""}
              {row.issuer ? ` · ${row.issuer}` : ""}
              {view === "dev" ? ` · ${row.subject_type} · ${row.category}` : ""}
            </div>
            <div className="mt-1"><SurfaceBadges surfaces={authoritySurfaces(row)} /></div>
            {row.public_status !== "approved" || row.evidence_status !== "verified" ? (
              <div className="admin-row__meta">검색에 아직 반영되지 않음</div>
            ) : null}
            <div className="admin-row__preview">{previewText(row.public_summary)}</div>
          </div>
          <button type="button" className="admin-row__btn" onClick={() => setEditing({ ...row })}>수정</button>
        </div>
      ))}
      <AdminEditDrawer open={Boolean(editing)} title="전문성 · 경력" onClose={() => setEditing(null)} footer={<button type="button" className="btn-primary" onClick={saveFact}>저장</button>}>
        {editing ? (
          <>
            <SeoLivePreview
              testPrefix="seo-edit"
              title={editing.public_title || editing.title}
              url={editing.verification_url || ""}
              description={editing.public_summary}
              ogTitle={editing.public_title || editing.title}
              ogDescription={editing.public_summary}
              ogUrl={editing.verification_url}
              aiItems={[
                { label: "공개 제목", value: editing.public_title || editing.title },
                { label: "검증", value: evidenceStatusLabel(editing, view) },
                { label: "공개", value: publicStatusLabel(editing, view) },
                { label: "발급 기관", value: editing.issuer },
                { label: "어디에 보이나", value: authoritySurfaces(editing).map((id) => ({ public: "공개 화면", search: "검색엔진", ai: "AI", admin: "관리자 전용" }[id])).join(" · ") },
              ]}
            />
            <div className="mb-3"><SurfaceBadges surfaces={authoritySurfaces(editing)} /></div>
            <AdminField label={view === "dev" ? "대상 (subject_type / subject_id)" : "대상"} hint="회사(organization) 또는 사람(person). 대표자 연결은 person / person-ceo. 공개 화면의 주체를 정합니다.">
              <input value={`${editing.subject_type || ""} / ${editing.subject_id || ""}`} onChange={(e) => {
                const [type, ...rest] = e.target.value.split("/");
                setEditing({ ...editing, subject_type: (type || "").trim(), subject_id: rest.join("/").trim() });
              }} />
            </AdminField>
            <AdminField label={view === "dev" ? "유형 (category)" : "유형"} hint="검색·AI가 이 사실을 어떤 종류의 전문성으로 이해할지 정합니다.">
              <select value={editing.category || "OTHER"} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {["COMPANY_CREDENTIAL","PERSON_CREDENTIAL","COMPANY_EXPERIENCE","PERSON_EXPERIENCE","PROJECT","GLOBAL_EXPERIENCE","INTELLECTUAL_PROPERTY","BUSINESS_CERTIFICATION","EDUCATION","LECTURE","TECHNICAL_TRAINING","AWARD","OTHER"].map((id) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "공개 제목 (public_title)" : "공개 제목"} hint="공개 화면과 검색엔진·AI에 보이는 제목입니다. 약 10~40자 권장."><input value={editing.public_title || ""} onChange={(e) => setEditing({ ...editing, public_title: e.target.value, title: editing.title || e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "Public Label / Value / Unit" : "숫자 표시 (이름 | 값 | 단위)"} hint="공개 화면에 숫자로 보여줄 때 사용합니다. 예: 누적 프로젝트 | 832 | 건">
              <input value={`${editing.public_label || ""} | ${editing.metric_value || ""} | ${editing.metric_unit || ""}`} onChange={(e) => {
                const [a, b, c] = e.target.value.split("|");
                setEditing({ ...editing, public_label: (a || "").trim(), metric_value: (b || "").trim(), metric_unit: (c || "").trim() });
              }} />
            </AdminField>
            <AdminField label={view === "dev" ? "공개 설명 (public_summary)" : "공개 설명"} hint="공개 화면과 AI가 참고하는 설명입니다. 과장 없이 확인된 내용만 적으세요. 약 40~160자 권장."><textarea rows={4} value={editing.public_summary || ""} onChange={(e) => setEditing({ ...editing, public_summary: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "발급/관련 기관 (issuer)" : "발급/관련 기관"} hint="인증서·교육을 준 기관입니다. 공개 화면에 보일 수 있습니다."><input value={editing.issuer || ""} onChange={(e) => setEditing({ ...editing, issuer: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "기간 (start_date | end_date | issued_date)" : "기간"} hint="시작일 / 종료일 / 발급일. 공개 화면에 기간으로 표시될 수 있습니다.">
              <input value={`${editing.start_date || ""} | ${editing.end_date || ""} | ${editing.issued_date || ""}`} onChange={(e) => {
                const [a, b, c] = e.target.value.split("|");
                setEditing({ ...editing, start_date: (a || "").trim(), end_date: (b || "").trim(), issued_date: (c || "").trim() });
              }} />
            </AdminField>
            <AdminField label={view === "dev" ? "Verification Type" : "검증 방법"} hint="이 사실을 어떻게 확인했는지입니다. 관리자 전용이며 공개 화면에는 직접 보이지 않습니다.">
              <select value={editing.verification_type || "DOCUMENT_HELD"} onChange={(e) => setEditing({ ...editing, verification_type: e.target.value })}>
                {["OFFICIAL_URL","PUBLIC_RECORD","DOCUMENT_HELD","PORTFOLIO","MULTI_SOURCE","OFFICIAL_PROFILE","INTERNAL_RECORD","OTHER"].map((id) => (
                  <option key={id} value={id}>{id}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "External Verification URL" : "외부 검증 주소"} hint="공식 검증 URL만 입력. 없으면 비워 두세요. 가짜 URL 금지. 공개되면 검색엔진·AI가 참고할 수 있습니다.">
              <input value={editing.verification_url || ""} onChange={(e) => setEditing({ ...editing, verification_url: e.target.value })} />
            </AdminField>
            <AdminField label={view === "dev" ? "Identity Match" : "본인 확인"} hint="배지 이름이 한나래와 다를 때 ‘확인 필요’. 확인 완료 후에만 공개됩니다.">
              <select value={editing.identity_match || "not_required"} onChange={(e) => setEditing({ ...editing, identity_match: e.target.value })}>
                <option value="not_required">{view === "dev" ? "not_required" : "본인 확인 불필요"}</option>
                <option value="pending">{view === "dev" ? "pending (확인 필요)" : "본인 확인 필요"}</option>
                <option value="confirmed">{view === "dev" ? "confirmed" : "본인 확인 완료"}</option>
                <option value="rejected">{view === "dev" ? "rejected" : "본인 확인 거절"}</option>
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "Official Name Status" : "공식 명칭 상태"} hint="자격증 공식 명칭이 확인되지 않으면 확인 필요. 추정 명칭(웹디자인기능사, GTQ 등) 금지. 확인 후에만 공개 가능.">
              <select value={editing.official_name_status || "not_required"} onChange={(e) => setEditing({ ...editing, official_name_status: e.target.value })}>
                <option value="not_required">{view === "dev" ? "not_required" : "확인 불필요"}</option>
                <option value="pending">{view === "dev" ? "pending (공식 명칭 확인 필요)" : "공식 명칭 확인 필요"}</option>
                <option value="confirmed">{view === "dev" ? "confirmed" : "공식 명칭 확인 완료"}</option>
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "Verification Status (evidence_status)" : "검증 상태"} hint="검증 완료여야 공개할 수 있습니다. 내부 증빙만 있으면 관리자 전용입니다.">
              <select value={editing.evidence_status || "private"} onChange={(e) => setEditing({ ...editing, evidence_status: e.target.value })}>
                <option value="private">{view === "dev" ? "private (미검증)" : "내부 증빙"}</option>
                <option value="verified">{view === "dev" ? "verified (검증됨)" : "검증 완료"}</option>
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "Public Status" : "공개 상태"} hint="공개 중만 홈페이지/검색/AI에 나갑니다. 초안·비공개는 검색에 아직 반영되지 않습니다. 검증 전에는 승인해도 저장 시 초안으로 내려갑니다.">
              <select value={editing.public_status || "hidden"} onChange={(e) => setEditing({ ...editing, public_status: e.target.value })}>
                <option value="hidden">{view === "dev" ? "hidden" : "비공개"}</option>
                <option value="draft">{view === "dev" ? "draft" : "초안 · 검색에 아직 반영되지 않음"}</option>
                <option value="approved">{view === "dev" ? "approved" : "공개 중"}</option>
              </select>
            </AdminField>
            <AdminField label={view === "dev" ? "관련 Capability" : "관련 역량"} hint="website / webdev / saas / admin / ai / automation / search. AI가 어느 서비스와 연결할지 참고합니다."><textarea rows={2} value={asLines(editing.related_capability_ids)} onChange={(e) => setEditing({ ...editing, related_capability_ids: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "관련 Service ID" : "연결된 서비스"} hint="이 사실과 연결할 서비스 ID입니다."><textarea rows={2} value={asLines(editing.related_services)} onChange={(e) => setEditing({ ...editing, related_services: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "관련 Guide ID" : "연결된 가이드"} hint="이 사실과 연결할 가이드 ID입니다."><textarea rows={2} value={asLines(editing.related_guides)} onChange={(e) => setEditing({ ...editing, related_guides: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "관련 Portfolio slug" : "연결된 포트폴리오"} hint="이 사실과 연결할 공개 프로젝트 주소 이름입니다."><textarea rows={2} value={asLines(editing.related_portfolios)} onChange={(e) => setEditing({ ...editing, related_portfolios: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "관련 Target Query ID" : "연결된 검색 질문"} hint="노출시키고 싶은 검색 질문과 이 사실을 연결합니다."><textarea rows={2} value={asLines(editing.related_target_queries)} onChange={(e) => setEditing({ ...editing, related_target_queries: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "entity id" : "항목 ID"} hint="개발자가 식별하는 ID입니다. 공개 화면에는 보이지 않습니다."><input value={editing.id || ""} onChange={(e) => setEditing({ ...editing, id: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "private evidence ids" : "내부 증빙 연결"} hint="관리자 전용 내부 증빙 ID입니다. 공개되지 않습니다."><textarea rows={2} value={asLines(editing.private_evidence_ids)} onChange={(e) => setEditing({ ...editing, private_evidence_ids: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "schema mapping" : "검색엔진 매핑"} hint="검색엔진용 회사 정보(JSON-LD)에 어떤 속성으로 연결되는지입니다. 예: Person.hasCredential"><input value={editing.schema_mapping || ""} onChange={(e) => setEditing({ ...editing, schema_mapping: e.target.value })} /></AdminField>
            {view === "dev" ? (
              <AdminField label="raw public data"><textarea rows={6} readOnly value={JSON.stringify({ id: editing.id, public_title: editing.public_title, public_summary: editing.public_summary, verification_type: editing.verification_type, identity_match: editing.identity_match, official_name_status: editing.official_name_status, public_status: editing.public_status }, null, 2)} /></AdminField>
            ) : null}
          </>
        ) : null}
      </AdminEditDrawer>

      <div className="flex items-center justify-between mt-8 mb-3">
        <h4 className="text-sm text-white/70">{view === "dev" ? "내부 증빙 (Private Evidence)" : "내부 증빙"}</h4>
        <button type="button" className="admin-row__btn" onClick={() => setPrivateDraft({ title: "", category: "OTHER", verification_type: "DOCUMENT_HELD", subject_type: "organization", subject_id: "org-dsitesolution", issuer: "", private_notes: "", related_fact_ids: [] })}>+ 내부 증빙</button>
      </div>
      <p className="text-xs text-white/40 mb-3">원본 파일은 이 단계에서 올리지 않습니다. 제목·기관·연결 사실만 기록하세요. 생년월일·전화·이메일·주소·서명·문서번호는 적지 마세요. 이 자료는 관리자 전용이며 공개 화면·검색·AI에 나가지 않습니다.</p>
      {privateItems.map((row) => (
        <div key={row.id} className="admin-row">
          <div className="admin-row__main">
            <div className="admin-row__title">{row.title}</div>
            <div className="admin-row__meta">{view === "dev" ? `${row.verification_type} · ${row.category} · 공개 안 함` : "내부 증빙 · 공개 안 함"}</div>
            <div className="mt-1"><SurfaceBadges surfaces={["admin"]} /></div>
          </div>
          <button type="button" className="admin-row__btn" onClick={() => setPrivateDraft({ ...row })}>수정</button>
        </div>
      ))}
      <AdminEditDrawer open={Boolean(privateDraft)} title="내부 증빙" onClose={() => setPrivateDraft(null)} footer={<button type="button" className="btn-primary" onClick={savePrivate}>저장</button>}>
        {privateDraft ? (
          <>
            <AdminField label={view === "dev" ? "자료명 (title)" : "자료명"} hint="관리자만 보는 내부 자료 이름입니다. 공개 화면에 보이지 않습니다."><input value={privateDraft.title || ""} onChange={(e) => setPrivateDraft({ ...privateDraft, title: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "유형 (category)" : "유형"} hint="관리자 분류입니다. 공개되지 않습니다."><input value={privateDraft.category || ""} onChange={(e) => setPrivateDraft({ ...privateDraft, category: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "기관 (issuer)" : "기관"} hint="자료를 발급한 기관입니다. 관리자 전용입니다."><input value={privateDraft.issuer || ""} onChange={(e) => setPrivateDraft({ ...privateDraft, issuer: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "Verification Type" : "검증 방법"} hint="내부에서 어떻게 확인했는지입니다. 공개되지 않습니다."><input value={privateDraft.verification_type || ""} onChange={(e) => setPrivateDraft({ ...privateDraft, verification_type: e.target.value })} /></AdminField>
            <AdminField label={view === "dev" ? "연결 Verified Fact ID" : "연결할 공개 사실"} hint="이 내부 증빙이 뒷받침하는 공개 사실 ID입니다."><textarea rows={2} value={asLines(privateDraft.related_fact_ids)} onChange={(e) => setPrivateDraft({ ...privateDraft, related_fact_ids: parseLines(e.target.value) })} /></AdminField>
            <AdminField label={view === "dev" ? "내부 메모 (공개 금지)" : "내부 메모"} hint="관리자만 봅니다. 개인정보·문서번호를 적지 마세요."><textarea rows={3} value={privateDraft.private_notes || ""} onChange={(e) => setPrivateDraft({ ...privateDraft, private_notes: e.target.value })} /></AdminField>
          </>
        ) : null}
      </AdminEditDrawer>
    </div>
  );
}

export function AuthorityEntityPanel() {
  const { view } = useSeoAdminView();
  return (
    <div>
      <p className="text-xs text-white/50 mb-4">
        {view === "dev"
          ? "없는 관계를 만들지 않습니다. 확인된 실적·등록만 마케팅에 사용하세요. 원본 서류는 내부 증빙에만 두고, 공개는 반드시 검증 후 공개 승인된 사실만 나갑니다."
          : "확인된 실적과 등록만 공개하세요. 원본 서류는 내부 증빙에만 두고, 검증 완료 + 공개 중인 사실만 홈페이지·검색·AI에 나갑니다."}
      </p>
      <AuthorityFactsPanel />
      <EntityKindPanel
        kind="people"
        title="대표자 / 핵심인력"
        fields={[
          { key: "name", label: "이름", hint: "공개 화면에 나올 수 있는 이름입니다. 확인된 인력만 적으세요." },
          { key: "role", label: "역할", hint: "예: 대표. 공개 화면과 AI가 역할을 이해하는 데 쓰입니다." },
          { key: "kind", label: "구분", hint: "company | representative | core | affiliate. 개발자 보기에서 영문 값을 유지합니다." },
          { key: "summary", label: "요약", area: true, hint: "이 사람이 무엇을 하는지 짧은 설명입니다. AI 이해에 쓰입니다. 약 40~120자 권장." },
          { key: "experience", label: "경력 (줄바꿈)", area: true, list: true, hint: "확인된 경력만 한 줄에 하나씩. 공개 승인 전까지 검색에 나가지 않을 수 있습니다." },
        ]}
      />
      <EntityKindPanel
        kind="credentials"
        title="인증 / 등록 / 검증 자료"
        fields={[
          { key: "title", label: "자료명", hint: "인증·등록 이름입니다. 검색엔진이 공식 근거로 참고할 수 있습니다." },
          { key: "issuer", label: "기관", hint: "발급 기관입니다. 예: 특허청, 중소벤처기업부." },
          { key: "country", label: "국가", hint: "발급 국가 코드입니다. 예: KR. 공개 화면에는 잘 보이지 않습니다." },
          { key: "issued_on", label: "발급/등록일", hint: "확인된 날짜만 적으세요. 예: 2026-07-31." },
          { key: "url", label: "URL", hint: "공식 확인 페이지 주소입니다. 가짜 URL 금지." },
          { key: "evidence", label: "증빙", area: true, hint: "관리자 메모입니다. 개인정보·문서번호는 적지 마세요." },
          { key: "proves", label: "증명하는 내용", area: true, hint: "이 자료가 무엇을 증명하는지입니다. AI·검색 근거 연결에 쓰입니다." },
        ]}
      />
      <EntityKindPanel
        kind="global-entities"
        title="글로벌 브랜드 / 해외 Entity"
        fields={[
          { key: "name", label: "이름", hint: "확인된 관계만 적으세요. 없는 관계를 만들지 마세요." },
          { key: "url", label: "URL", hint: "공식 페이지 주소입니다." },
          { key: "country", label: "국가", hint: "관련 국가 코드입니다." },
          { key: "relation_status", label: "관계", hint: "관계 없음 / 확인 필요 / 실제 참여 / 대표자 경력 / 과거 사업 / 관계사 / 브랜드 연관 / 기타" },
          { key: "summary", label: "설명", area: true, hint: "관계를 과장하지 말고 확인된 내용만 적으세요." },
          { key: "source_url", label: "출처 URL", hint: "이 관계를 확인할 수 있는 공개 주소입니다." },
        ]}
      />
    </div>
  );
}
